/* eslint-disable functional/no-loop-statement */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */
import { RenderContent } from '@/components/blocks/RenderContent';
import { CatalogEntryCarousel } from '@/components/content/CatalogEntryRecommendation/parts/CatalogEntryCarousel';
import { CatalogEntryCarouselSearch } from '@/components/content/CatalogEntryRecommendation/parts/CatalogEntryCarouselSearch';
import { ProductLite } from '@/components/content/Product';
import { ProductSearch } from '@/components/content/ProductSearch';
import { useCatalogEntryRecommendation } from '@/data/Content/CatalogEntryRecommendation';
import { useProducts } from '@/data/Content/Products';
import { useContentEvents } from '@/data/Content/_ContentEvents';
import { useProductsWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';
import { useUser } from '@/data/User';
import { HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL } from '@/data/constants/product';
import { ContentProvider } from '@/data/context/content';
import { ID } from '@/data/types/Basic';
import { ProductType } from '@/data/types/Product';
import { WidgetProperties } from '@/data/types/Slot';
import { getContractIdFromContext } from '@/utils/getContractIdFromContext';
import { Skeleton } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

interface InfoElectronico {
	offerDeContado?: string;
	strikedDeContado?: string;
}

interface ProductTypeWithInfoElectronico extends ProductType {
	infoElectronico?: InfoElectronico;
}

const cardContainerMapper = (
	products: ProductTypeWithInfoElectronico[],
	contract: string,
	clickAction: (partNumber: string) => void
) =>
	products.map((product, index) => ({
		partNumber: product.partNumber,
		card: (
			<ProductLite
				key={`${contract}_${product.partNumber}_${index}`}
				product={product}
				contract={contract}
				infoElectronico={product?.infoElectronico}
				clickAction={clickAction.bind(null, product.partNumber)}
			/>
		),
		a11y: { 'aria-label': undefined, 'aria-labelledby': product.partNumber },
	}));

const cardSkeletonContainerMapper = (partNumbers: string[]) =>
	partNumbers.map((partNumber) => ({
		partNumber,
		card: <Skeleton variant="rectangular" width={290} height={440} />,
		a11y: { 'aria-label': undefined, 'aria-labelledby': partNumber },
	}));

export const cardSkeletonSearchContainerMapper = (partNumbers: string[]) =>
	partNumbers.map((partNumber) => ({
		partNumber,
		card: <Skeleton variant="rectangular" width={202} height={295} />,
		a11y: { 'aria-label': undefined, 'aria-labelledby': partNumber },
	}));

const cardSearchContainerMapper = (
	products: ProductTypeWithInfoElectronico[],
	contract: string,
	clickAction: (partNumber: string) => void,
	onClick?: () => void
) =>
	products.map((product, index) => ({
		partNumber: product.partNumber,
		card: (
			<ProductSearch
				key={`${contract}_${product.partNumber}_${index}`}
				product={product}
				infoElectronico={product?.infoElectronico}
				clickAction={() => {
					clickAction(product.partNumber);
					onClick?.();
				}}
			/>
		),
		a11y: { 'aria-label': undefined, 'aria-labelledby': product.partNumber },
	}));

const emptyProperties = {} as WidgetProperties;
const EMPTY_PARTNUMBERS: string[] = [];

export const CatalogEntryRecommendation: FC<{
	id: ID;
	properties?: WidgetProperties;
	loadingElement?: JSX.Element;
	isSearch?: boolean;
	initialHidden?: boolean;
	onClick?: () => void;
}> = ({
	id: _id,
	properties = emptyProperties,
	loadingElement,
	initialHidden,
	isSearch,
	onClick,
}) => {
	const { emsName = '' } = properties;
	const {
		partNumbers = EMPTY_PARTNUMBERS,
		clickAction,
		loading,
		title,
	} = useCatalogEntryRecommendation(emsName);

	const { onContentClick } = useContentEvents();
	const { user } = useUser();

	const [contract, setContract] = useState<string>(getContractIdFromContext(user?.context));
	const [invalid, setInvalid] = useState<Record<string, boolean>>({});

	const idSpot = isSearch && typeof _id === 'string' && _id.includes('DPV2_Search_Recommendation');

	const { products, loading: loadingProducts } = useProducts({
		partNumber: partNumbers,
		contractId: contract,
		query: { profileName: HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL },
	});

	const productsOrdered = useMemo(
		() =>
			products.sort((a, b) => {
				a.showPrice = false;
				const aIndex = partNumbers.indexOf(a.partNumber);
				const bIndex = partNumbers.indexOf(b.partNumber);
				return aIndex - bIndex;
			}),
		[products, partNumbers]
	);

	const { data: productsWithInfoElectronico } = useProductsWithInfoElectronico(productsOrdered);

	const original = useMemo(() => {
		if (idSpot && loadingProducts) {
			return cardSkeletonSearchContainerMapper(partNumbers);
		}

		if (idSpot) {
			return cardSearchContainerMapper(productsOrdered, contract, clickAction, onClick);
		}

		if (loadingProducts) {
			return cardSkeletonContainerMapper(partNumbers);
		}

		if (
			!loadingProducts &&
			productsOrdered.length > 0 &&
			productsWithInfoElectronico &&
			productsWithInfoElectronico.length === 0
		) {
			return cardContainerMapper(productsOrdered, contract, clickAction);
		} else {
			return cardContainerMapper(productsWithInfoElectronico, contract, clickAction);
		}
	}, [
		idSpot,
		partNumbers,
		contract,
		productsWithInfoElectronico,
		productsOrdered,
		loadingProducts,
		clickAction,
		onClick,
	]);

	const { slides } = useMemo(() => {
		const filtered = original.filter(({ partNumber }) => !invalid[`${partNumber}_${contract}`]);
		const slides = filtered.map(({ card }) => card);
		const a11yProps = filtered.map(({ a11y }) => a11y);
		return { slides, a11yProps };
	}, [invalid, original, contract]);

	const onNotify = useCallback(
		(id: string, usedContract: string, product: ProductType) =>
			setInvalid((old) => ({ ...old, [`${id}_${usedContract}`]: !product })),
		[]
	);

	useEffect(() => {
		// we only need to trigger updates on changes, not initial loads -- initial load updates are
		//   taken care of by the initial render's invocations of `onNotify`
		const _contract = getContractIdFromContext(user?.context);
		if (contract !== _contract) {
			setContract(_contract);
		}
	}, [user?.context]); // eslint-disable-line react-hooks/exhaustive-deps

	if (loading) {
		return loadingElement || null;
	}

	return (
		<ContentProvider value={{ onNotify }}>
			{title?.map((content) => (
				<RenderContent
					key={`${content.id}${content.contentId}`}
					content={content}
					onClick={onContentClick(content)}
				/>
			))}
			{idSpot ? (
				<CatalogEntryCarouselSearch initialHidden={initialHidden} slides={slides} id={emsName} />
			) : (
				<CatalogEntryCarousel slides={slides} id={emsName} />
			)}
		</ContentProvider>
	);
};
